/**
 * InitialReviewBanner
 *
 * Prominent banner that appears above the Case Overview card when there's
 * a pending "Conduct Initial Review" task. Encourages the user to complete
 * the initial review before proceeding with the case.
 */
import { ArrowRight, ClipboardCheck } from 'lucide-react';
import { useCallback, useState } from 'react';

import { InitialReviewModal } from '@/components/initial-review';
import { Button } from '@/components/ui/button';
import {
  INITIAL_REVIEW_EVENTS,
  INITIAL_REVIEW_PROPERTIES,
} from '@/constants/analytics/initial-review-events';
import { usePostHogCapture } from '@/hooks/usePostHogCapture';
import { useGetMatterDetailsQuery } from '@/services/api/mattersService';
import { useGetTasksQuery } from '@/services/api/tasksService';
import { MatterStatus } from '@/services/types/client-intake-types';
import {
  CanonicalTaskStatus,
  CanonicalTaskType,
} from '@/services/types/task-types';

interface InitialReviewBannerProps {
  matterId: string;
}

export const InitialReviewBanner = ({ matterId }: InitialReviewBannerProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const capture = usePostHogCapture();

  // Fetch matter to check status
  const { data: matter } = useGetMatterDetailsQuery(matterId, {
    skip: !matterId,
  });

  // Fetch tasks to find pending initial review task
  const { data: tasksData } = useGetTasksQuery(
    { matter: matterId, limit: 100 },
    { skip: !matterId },
  );

  // Find the initial review task (if it exists)
  const initialReviewTask = tasksData?.results?.find(
    (task) =>
      task.type?.canonical_type === CanonicalTaskType.CONDUCT_INITIAL_REVIEW,
  );

  // Task is pending if it exists and is not completed/N/A
  const isTaskPending =
    initialReviewTask &&
    initialReviewTask.status?.canonical_status !==
      CanonicalTaskStatus.COMPLETED &&
    initialReviewTask.status?.canonical_status !==
      CanonicalTaskStatus.NOT_APPLICABLE;

  // Show banner if matter is in Initial Review status OR task is pending
  const isInitialReviewStatus = matter?.status === MatterStatus.INITIAL_REVIEW;
  const shouldShow = isInitialReviewStatus || isTaskPending;

  // Handle banner CTA click with analytics
  const handleStartReview = useCallback(() => {
    capture(INITIAL_REVIEW_EVENTS.BANNER_CTA_CLICKED, {
      [INITIAL_REVIEW_PROPERTIES.MATTER_ID]: matterId,
      [INITIAL_REVIEW_PROPERTIES.TASK_ID]: initialReviewTask?.id,
    });
    setIsModalOpen(true);
  }, [capture, matterId, initialReviewTask?.id]);

  if (!shouldShow) {
    return null;
  }

  return (
    <>
      <div className="rounded-md border border-blue-200 dark:border-blue-900 bg-blue-50 dark:bg-blue-950 overflow-hidden">
        <div className="px-3 py-3 flex items-center justify-between gap-3">
          {/* Left: Icon and text */}
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-blue-100 dark:bg-blue-900">
              <ClipboardCheck className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-100">
                Initial Review Required
              </h3>
              <p className="text-xs font-medium text-blue-700 dark:text-blue-300 mt-0.5">
                Verify case details, review tasks, and prepare follow-up notes
              </p>
            </div>
          </div>

          {/* Right: CTA button */}
          <Button
            onClick={handleStartReview}
            size="sm"
            className="flex-shrink-0 gap-1.5 bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-500"
          >
            Start Review
            <ArrowRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      <InitialReviewModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        matterId={matterId}
        taskId={initialReviewTask?.id}
      />
    </>
  );
};
